import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import FindNetSuiteVendorPopup from '../../../components/FindNetSuiteVendorPopup';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';

import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import useSnackBar from '../../../hooks/useSnackBar';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import { DEDUCTION_TYPES, INTENT_TYPE_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../../../constants/constants';
import FormPDFUploadField from '../../../components/formFields/FormPDFUploadField';
import { DEDUCTIONS_EDIT_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { deductionsEditFormSchema } from '../../../helpers/formSchemas/formSchemas';
import {
  deleteDocument,
  getDocument,
  getDeductionRecord,
  getRemittanceContextByDeduction,
  updateDeductionRecord,
} from '../../../api/paymentsApi/deductionsApi';
import { DeductionEditFormSubmitProps } from '../types';
import FormToggleField from '../../../components/formFields/FormToggleField';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import { useHasPermission } from '../../../hooks/useHasPermission';
import FormAutocompleteFieldServerSearch from '../../../components/formFields/FormAutocompleteFieldServerSearch';
import { handleGetDefendants, mapDefendantsQueryResults } from '../helpers';
import { mapVendorsQueryResults } from '../../CasesView/helpers';
import {
  ActiveVendorAccountOption,
  getActiveVendorAccounts,
  getVendorPaymentMethodDetails,
} from '../../../api/vendorApi/vendorAccountsApi';
import { ErrorData, PaginationQueryParams, SelectOption } from '../../../types';
import Popup from '../../../components/Popup';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import FormDateField from '../../../components/formFields/FormDateField';
import moment from 'moment';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { AxiosError } from 'axios';
import { getVendorDefaultPaymentAccountId, getVendorPaymentAccountOptions } from '../../../helpers/vendorPaymentAccounts';
import RemittanceAccordion from '../DeductionsView/RemittanceAccordion';
import CommonButton from '../../../components/buttons/CommonButton';
import { renderPaymentAccountInfo, renderPaymentAccountOption, renderPaymentAccountSelectedValue } from '../paymentAccountDisplayHelpers';
import { accountingVendorType } from '../../VendorsView/helpers';

const DEDUCTION_PAYMENT_METHOD_OPTIONS = PAYMENT_METHOD_OPTIONS.filter(
  (option) => String(option.id).toUpperCase() !== 'V',
);

interface EditDeductionPopupProps {
  isOpen: boolean;
  loading?: boolean;
  onSuccess?: () => void;
  onFailed?: () => void;
  onCancel: () => void;
  settlementAmount?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialData?: any;
  dataIndex?: number | null;
  caseId?: string;
  canEdit?: boolean;
  isAccountingIntegrated?: boolean;
}

interface RemittanceLine {
  id?: number | string;
  sequence?: number;
  amount: number | null;
  values: Record<string, string>;
}

const useStyles = makeStyles()((theme: Theme) => ({
  borderedBox: {
    width: '96%',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    padding: '10px 7px 10px 7px',
    marginTop: '16px',
    background: '#2e3243',
  },
  settlementSummaryBox: {
    width: '90%',
    background: '#2e3243',
    borderRadius: '12px',
    padding: '20px 24px',
    marginBottom: theme.spacing(2),
  },
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.light,
      maxWidth: '950px',
      minWidth: '950px',
      width: '100%',
      padding: '32px',
      boxSizing: 'border-box',
      borderRadius: '16px',
      maxHeight: '95%',
    },

    '& .MuiDialogTitle-root': {
      padding: 0,
      fontSize: '34px',
      fontWeight: 600,
      lineHeight: '120%',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
    },

    '& .MuiDialogContent-root': {
      padding: 0,
      display: 'flex',
      justifyContent: 'center',
      overflowY: 'auto',

      '&::-webkit-scrollbar': {
        width: '5px',
        borderRadius: '20px',
      },

      '&::-webkit-scrollbar-thumb': {
        borderRadius: 20,
        backgroundColor: theme.palette.secondary.dark,
      },
    },

    '& .MuiDialogContentText-root': {
      color: theme.palette.secondary.main,
    },
  },
  popupActions: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '416px',
    width: '100%',
    margin: '0 auto',
    gap: '16px',
    padding: 0,
  },
  popupButton: {
    color: theme.palette.info.main,

    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.3)',
    },

    '&:hover': {
      background: 'rgba(144, 202, 249, 0.08)',
    },
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '800px',
    minWidth: '800px',
    width: '100%',
  },
  formFieldsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    alignItems: 'center',
    padding: '32px 0 32px 0',
  },
  checkboxWrapper: {
    width: '100%',

    '& label': {
      marginRight: 0,
    },
  },
}));

const EditDeductionPopup = ({
  isOpen,
  loading,
  onSuccess,
  onCancel,
  initialData,
  caseId,
  settlementAmount,
  canEdit,
  isAccountingIntegrated,
}: EditDeductionPopupProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const canViewDefendants = useHasPermission('Defendants', 'View');
  const canViewVendors = useHasPermission('Vendors', 'View');
  const vendorEntity = accountingVendorType

  const theme = useTheme();

  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [isDeductionLoading, setIsDeductionLoading] = useState<boolean>(false);
  const [isNetSuitePopupOpen, setIsNetSuitePopupOpen] = useState<boolean>(false);
  const [isSelectingNetSuiteVendor, setIsSelectingNetSuiteVendor] = useState<boolean>(false);

  const [deductionData, setDeductionData] = useState<{ [key: string]: unknown }>();
  const [deductionId, setDeductionId] = useState<string | null>(null);

  const [activeVendors, setActiveVendors] = useState<ActiveVendorAccountOption[]>([]);

  const [vendorPaymentAccountsById, setVendorPaymentAccountsById] = useState<
    Record<
      string,
      {
        achAccounts?: ActiveVendorAccountOption['achAccounts'];
        checkAccounts?: ActiveVendorAccountOption['checkAccounts'];
      }
    >
  >({});

  const [paymentAccountOptions, setPaymentAccountOptions] = useState<Array<{
    id: string;
    label: string;
    subtitle?: string;
    isLastUsedAccount?: boolean;
    isUsedAccount?: boolean;
    isNotUsedAccount?: boolean;
    isFailedAccount?: boolean;
  }>>([]);
  const [remittanceContext, setRemittanceContext] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [remittanceTemplate, setRemittanceTemplate] = useState<any | null>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [hasRequestedRemittanceContext, setHasRequestedRemittanceContext] = useState(false);


  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(deductionsEditFormSchema),
    defaultValues: DEDUCTIONS_EDIT_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { isValid, isDirty, dirtyFields },
  } = formMethods;

  const isAssigned = watch('isAssigned');
  const typeFieldValue = watch('type');
  const vendorAccountIdValue = watch('vendorAccount.id');
  const vendorAccountValue = watch('vendorAccount');
  const paymentMethodValue = watch('paymentMethod');
  const paymentAccountValue = watch('paymentAccount');
  const defendantIdValue = watch('defendant.id');
  const preSettlementAmountValue = watch('preSettlementAmount');
  const settlementAmountValue = watch('settlementAmount');
  const amountValue = watch('amount') ?? '';
  const dispositionValue = watch('disposition');
  const requiresPaymentAccount =
    dispositionValue !== 'External' &&
    ['ACH', 'CHECK'].includes((paymentMethodValue || '').toUpperCase());

  const isPopupLoading = loading || isDeductionLoading || isSelectingNetSuiteVendor || !deductionData;

  const isVendorType = typeFieldValue === 'Lien' || typeFieldValue === 'SERVICE_PROVIDER';

  const isVendorAccountMissing = isVendorType && !vendorAccountIdValue;

  const prevDispositionRef = useRef<string | undefined>(undefined);
  const isInitialLoadRef = useRef<boolean>(true);
  const previousDeductionAmountRef = useRef<string | null>(null);

  const selectedPaymentAccount = paymentAccountOptions.find(
    (item) => item.id === String(paymentAccountValue?.id),
  );

  const [isPaymentMethodMissing, setIsPaymentMethodMissing] = useState(false);
  const [isPaymentProvisioningIssue, setIsPaymentProvisioningIssue] = useState(false);
  const [isVendorsLoaded, setIsVendorsLoaded] = useState(false);
  const [isDeleteInvoicePopupOpen, setIsDeleteInvoicePopupOpen] = useState<boolean>(false);
  const [isFailedAccountConfirmOpen, setIsFailedAccountConfirmOpen] = useState<boolean>(false);
  const pendingSubmitValuesRef = useRef<DeductionEditFormSubmitProps | null>(null);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<string>('');
  const [canUpdate, setCanUpdate] = useState<boolean>(false);
  const [isRemittanceFormOpen, setIsRemittanceFormOpen] = useState(false);

  const handleCloseDeleteInvoicePopup = () => setIsDeleteInvoicePopupOpen(false);

  const handleOpenDeleteInvoicePopup = () => setIsDeleteInvoicePopupOpen(true);

  const onDeleteInvoiceButtonClick = () => handleOpenDeleteInvoicePopup();

  const isCheckPayment = (paymentMethodValue || '').toUpperCase() === 'CHECK';

  const remittanceLinesValue = watch('remittanceLines') || [];

  const remittanceTotal = Array.isArray(remittanceLinesValue)
    ? remittanceLinesValue.reduce((total, line) => {
      if (typeof line.amount === 'number' && Number.isFinite(line.amount)) {
        return total + line.amount;
      }

      return total;
    }, 0)
    : 0;

  const deductionAmountNumber = Number(amountValue || 0);
  const isCheckRemittanceBalanced =
    (paymentMethodValue || '').toUpperCase() === 'CHECK' && !!remittanceContext && !!remittanceTemplate
      ? Math.round(deductionAmountNumber * 100) === Math.round(remittanceTotal * 100)
      : true;


  const buildDefaultRemittanceValues = (
    template: {
      columns?: Array<{ binding: string; key: string }>;
    } | null,
    defaults: Record<string, string>,
  ): Record<string, string> => {
    const columns = template && Array.isArray(template?.columns) ? template.columns : [];

    const values: Record<string, string> = {};

    for (const column of columns) {
      if (column.binding === 'LINE_AMOUNT') {
        continue;
      }

      values[column.key] = defaults[column.key] ?? '';
    }

    return values;
  };

  const getDefaultRemittanceLine = (
    template: {
      columns?: Array<{ binding: string; key: string }>;
    } | null,
    defaults: Record<string, string>,
  ) => {
    const rawAmount = String(amountValue ?? '').trim();

    const parsedAmount = rawAmount ? Number(rawAmount.replace(/[$,\s]/g, '')) : null;

    return {
      id: 1,
      amount: Number.isFinite(parsedAmount as number) ? parsedAmount : null,
      values: buildDefaultRemittanceValues(template, defaults),
    };
  };

  useEffect(() => {
    const currentAmountValue = String(amountValue ?? '');

    const previousAmountValue = previousDeductionAmountRef.current;

    previousDeductionAmountRef.current = currentAmountValue;

    if (previousAmountValue === null) {
      return;
    }

    if (previousAmountValue === currentAmountValue) {
      return;
    }

    if (!isCheckPayment || !remittanceContext || !remittanceTemplate) {
      return;
    }

    const currentLines = getValues('remittanceLines') || [];

    if (!Array.isArray(currentLines) || currentLines.length !== 1) {
      return;
    }

    const rawAmount = currentAmountValue.trim();

    const parsedAmount = rawAmount
      ? Number(rawAmount.replace(/[$,\s]/g, ''))
      : null;

    const nextAmount =
      parsedAmount !== null && Number.isFinite(parsedAmount)
        ? parsedAmount
        : null;

    const firstLine = currentLines[0];

    if (firstLine.amount === nextAmount) {
      return;
    }

    setValue(
      'remittanceLines',
      [
        {
          ...firstLine,
          amount: nextAmount,
        },
      ],
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  }, [
    amountValue,
    isCheckPayment,
    remittanceContext,
    remittanceTemplate,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (!deductionId || !isVendorType || !vendorAccountIdValue || !isCheckPayment) {
      setHasRequestedRemittanceContext(false);
      setIsContextLoading(false);
      setRemittanceContext(null);
      setRemittanceTemplate(null);

      return;
    }

    setHasRequestedRemittanceContext(true);
    setIsContextLoading(true);

    getRemittanceContextByDeduction(deductionId)
      .then((response) => {
        const context = response?.data ?? null;

        const hasContext = !!context && !!context.remittanceTemplate;

        const hasContextLines = Array.isArray(context?.remittanceLines) && context.remittanceLines.length > 0;

        const defaultLine = hasContext
          ? getDefaultRemittanceLine(context.remittanceTemplate, context.prefetchedValues ?? {})
          : null;

        const existingLines = getValues('remittanceLines') || [];

        const nextRemittanceLines =
          existingLines.length > 0
            ? existingLines
            : hasContextLines
              ? context.remittanceLines
              : hasContext && defaultLine
                ? [defaultLine]
                : [];

        setRemittanceContext(context ?? null);
        setRemittanceTemplate(context?.remittanceTemplate ?? null);

        setValue('remittanceLines', nextRemittanceLines, {
          shouldDirty: false,
          shouldValidate: true,
        });
      })
      .catch(() => {
        setRemittanceContext(null);
        setRemittanceTemplate(null);
      })
      .finally(() => {
        setIsContextLoading(false);
      });
  }, [deductionId, isVendorType, vendorAccountIdValue, isCheckPayment]);

  useEffect(() => {
    if (!isVendorsLoaded) {
      setIsPaymentMethodMissing(false);
      return;
    }

    if ((paymentMethodValue || '').toUpperCase() === 'V') {
      setIsPaymentMethodMissing(false);
      return;
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

    const selectedActivePaymentMethod = selectedVendor?.activePaymentMethod || [];

    setIsPaymentMethodMissing(!!paymentMethodValue && !selectedActivePaymentMethod.includes(paymentMethodValue));
  }, [paymentMethodValue, vendorAccountIdValue, activeVendors, isVendorsLoaded]);

  useEffect(() => {
    if (!isVendorsLoaded) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));
    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();
    const selectedPaymentAccountId = paymentAccountValue?.id ? String(paymentAccountValue.id) : '';

    if (!selectedVendor || !selectedPaymentAccountId || !requiresPaymentAccount) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const provisioningList =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendor.activeBankAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendor.activeMailingAddresses || []
          : [];

    if (!provisioningList.length) {
      setIsPaymentProvisioningIssue(false);
      return;
    }

    const selectedProvisioningAccount = provisioningList.find((account) => String(account.id) === selectedPaymentAccountId);

    setIsPaymentProvisioningIssue(!!selectedProvisioningAccount && selectedProvisioningAccount.provisionStatus !== 'Completed');
  }, [paymentMethodValue, paymentAccountValue?.id, vendorAccountIdValue, activeVendors, isVendorsLoaded, requiresPaymentAccount]);

  const applyVendorPaymentDefaults = async (
    selectedVendor: SelectOption | null,
    shouldDirty = true,
  ) => {
    const vendorId = selectedVendor?.id ? String(selectedVendor.id) : '';

    setValue('paymentAccount', null, {
      shouldDirty,
      shouldValidate: true,
    });

    if (!vendorId) {
      setValue('paymentMethod', '', {
        shouldDirty,
        shouldValidate: true,
      });
      setDefaultPaymentMethod('');
      return;
    }

    const selectedActiveVendor = activeVendors.find(
      (vendor) => String(vendor.id) === vendorId,
    );

    const fallbackDefaultPaymentMethod =
      selectedVendor?.defaultPaymentMethod ||
      selectedActiveVendor?.defaultVendorPaymentMethod ||
      '';

    setValue('paymentMethod', fallbackDefaultPaymentMethod, {
      shouldDirty,
      shouldValidate: true,
    });

    const parsedVendorId = Number(vendorId);

    if (!Number.isFinite(parsedVendorId)) {
      setDefaultPaymentMethod(fallbackDefaultPaymentMethod);
      return;
    }

    try {
      const response = await getVendorPaymentMethodDetails(parsedVendorId);

      const latestVendorAccount = getValues('vendorAccount');
      if (String(latestVendorAccount?.id || '') !== vendorId) {
        return;
      }

      setVendorPaymentAccountsById((prev) => ({
        ...prev,
        [vendorId]: {
          achAccounts: response.data.achAccounts,
          checkAccounts: response.data.checkAccounts,
        },
      }));

      const apiDefaultPaymentMethod =
        response.data.defaultVendorPaymentMethod || fallbackDefaultPaymentMethod;

      setValue('paymentMethod', apiDefaultPaymentMethod, {
        shouldDirty,
        shouldValidate: true,
      });

      setDefaultPaymentMethod(apiDefaultPaymentMethod);

      setValue(
        'vendorAccount',
        {
          id: vendorId,
          label: latestVendorAccount?.label || selectedVendor?.label || '',
          defaultPaymentMethod: apiDefaultPaymentMethod,
          accounting: latestVendorAccount?.accounting || selectedVendor?.accounting,
        },
        {
          shouldDirty: false,
          shouldValidate: false,
        },
      );
    } catch {
      const latestVendorAccount = getValues('vendorAccount');
      if (String(latestVendorAccount?.id || '') !== vendorId) {
        return;
      }

      setValue('paymentMethod', fallbackDefaultPaymentMethod, {
        shouldDirty,
        shouldValidate: true,
      });
      setDefaultPaymentMethod(fallbackDefaultPaymentMethod);
    }
  };

  useEffect(() => {
    if (initialData) {
      setDeductionId(initialData.id as string);
    }
  }, [initialData]);

  useEffect(() => {
    if (!isOpen || !deductionId) {
      return;
    }

    setIsDeductionLoading(true);
    isInitialLoadRef.current = true;

    getDeductionRecord(deductionId)
      .then((response) => {
        setDeductionData(response.data);

        const existingRemittanceLines: RemittanceLine[] = Array.isArray(response.data.remittanceLines)
          ? response.data.remittanceLines
          : [];

        previousDeductionAmountRef.current = String(response.data.amount ?? '');

        const hasDefendant = response.data.defendant !== null && Boolean(response.data.defendant.name.length);

        const type = response.data.type === 'Advance' ? 'Expense Reimbursement' : response.data.type;

        const vendor = response.data.vendor && {
          id: response.data.vendor.id,
          label: response.data.vendor.name,
          defaultPaymentMethod:
            response.data.paymentMethod && response.data.paymentMethod !== '-' ? response.data.paymentMethod : null,
          accounting: response.data.vendor.accounting,
        };

        const vendorAccount = type === 'Lien' || type === 'SERVICE_PROVIDER' ? vendor : null;

        const initialPaymentAccountId = response.data.vendorPaymentAccountId ?? response.data.paymentAccountId;

        const initialPaymentAccountName = response.data.vendorPaymentAccountName ?? response.data.paymentAccountName;

        const canUpdate = response.data.canUpdate as boolean;
        setCanUpdate(canUpdate);

        reset({
          id: response.data.id,
          caseReferenceId: response.data.case.id,
          caseSystemName: response.data.case.name,
          isAssigned: hasDefendant,
          amount: response.data.amount,
          type,
          defendant: hasDefendant
            ? {
              id: response.data.defendant.id,
              label: response.data.defendant.name,
            }
            : {
              id: '',
              label: '',
            },
          disposition: response.data.disposition,
          vendorAccount,
          // description: response.data.description || '',
          memo: response.data.memo || '',
          paymentAccount: initialPaymentAccountId
            ? {
              id: String(initialPaymentAccountId),
              label: String(initialPaymentAccountName || ''),
            }
            : null,
          paymentMethod: response.data.paymentMethod && response.data.paymentMethod !== '-' ? response.data.paymentMethod : null,
          uploadInvoice: null,
          preSettlementAmount: response.data.preSettlementAmount ?? '',
          settlementAmount,
          entryDate: response.data.entryDate ? moment(response.data.entryDate) : undefined,
          externalPaymentReference: response.data.externalPaymentReference || '',
          externalPaymentDate: response.data.externalPaymentDate
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (moment(response.data.externalPaymentDate) as any)
            : undefined,
          remittanceLines: existingRemittanceLines,
        });

        formMethods.trigger();

        prevDispositionRef.current = response.data.disposition;

        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 0);
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.message || 'Failed to load account data',
          type: 'error',
        }));

        onCancel();
      })
      .finally(() => {
        setIsDeductionLoading(false);
      });
  }, [deductionId, reset, isOpen, settlementAmount]);

  const handleGetActiveVendors = async (params: PaginationQueryParams, type: string) => {
    let vendorTypeFilter: string | undefined;

    if (type === 'Lien') {
      vendorTypeFilter = 'LIEN_HOLDER';
    } else if (type === 'SERVICE_PROVIDER') {
      vendorTypeFilter = 'SERVICE_PROVIDER';
    }

    const filters = vendorTypeFilter ? { vendorType: vendorTypeFilter } : undefined;

    const response = await getActiveVendorAccounts(params.match, filters);

    setActiveVendors(response?.data);
    setIsVendorsLoaded(true);

    return {
      data: {
        resultSet: response.data,
        totalCount: response.data.length,
      },
    };
  };

  useEffect(() => {
    if (vendorAccountIdValue && isVendorsLoaded && activeVendors.length > 0) {
      const foundVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

      if (foundVendor) {
        if (foundVendor.defaultVendorPaymentMethod) {
          setDefaultPaymentMethod(foundVendor.defaultVendorPaymentMethod);
        }

        if (vendorAccountValue) {
          setValue(
            'vendorAccount',
            {
              id: vendorAccountValue?.id || '',
              label: vendorAccountValue?.label || '',
              defaultPaymentMethod: vendorAccountValue.defaultPaymentMethod || foundVendor.defaultVendorPaymentMethod,
              accounting: foundVendor.accounting ?? vendorAccountValue.accounting,
            },
            {
              shouldDirty: false,
              shouldValidate: false,
            },
          );
        }
      }
    }
  }, [vendorAccountIdValue, activeVendors, isVendorsLoaded, setValue]);

  useEffect(() => {
    if (!vendorAccountIdValue || !paymentMethodValue || !requiresPaymentAccount) {
      setPaymentAccountOptions([]);

      setValue('paymentAccount', null, {
        shouldDirty: false,
        shouldValidate: true,
      });

      return;
    }

    const vendorIdKey = String(vendorAccountIdValue);
    const vendorId = Number(vendorAccountIdValue);

    if (!vendorPaymentAccountsById[vendorIdKey] && Number.isFinite(vendorId)) {
      getVendorPaymentMethodDetails(vendorId)
        .then((response) => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: response.data.achAccounts,
              checkAccounts: response.data.checkAccounts,
            },
          }));
        })
        .catch(() => {
          setVendorPaymentAccountsById((prev) => ({
            ...prev,
            [vendorIdKey]: {
              achAccounts: [],
              checkAccounts: [],
            },
          }));
        });
    }

    const selectedVendor = activeVendors.find((vendor) => String(vendor.id) === String(vendorAccountIdValue));

    const selectedVendorAccounts = vendorPaymentAccountsById[vendorIdKey];

    const vendorForPaymentAccounts = selectedVendor
      ? {
        ...selectedVendor,
        achAccounts: selectedVendorAccounts?.achAccounts,
        checkAccounts: selectedVendorAccounts?.checkAccounts,
      }
      : selectedVendorAccounts;

    const normalizedPaymentMethod = (paymentMethodValue || '').trim().toUpperCase();

    const accountDetails: Array<{
      id: number | string;
      isLastUsedAccount?: boolean;
      isUsedAccount?: boolean;
      isNotUsedAccount?: boolean;
      isFailedAccount?: boolean;
    }> =
      normalizedPaymentMethod === 'ACH'
        ? selectedVendorAccounts?.achAccounts || []
        : normalizedPaymentMethod === 'CHECK'
          ? selectedVendorAccounts?.checkAccounts || []
          : [];

    const options = getVendorPaymentAccountOptions(
      vendorForPaymentAccounts,
      paymentMethodValue,
    ).map((item) => {
      const account = accountDetails.find(
        (account) => String(account.id) === String(item.id),
      );

      return {
        id: String(item.id),
        label: item.subtitle
          ? `${item.label} - ${item.subtitle}`
          : item.label,
        subtitle: item.subtitle,
        isLastUsedAccount: account?.isLastUsedAccount,
        isUsedAccount: account?.isUsedAccount,
        isNotUsedAccount: account?.isNotUsedAccount,
        isFailedAccount: account?.isFailedAccount,
      };
    });

    setPaymentAccountOptions(options);

    const currentOption = options.find(
      (item) => item.id === String(paymentAccountValue?.id),
    );

    if (currentOption && paymentAccountValue?.label) {
      return;
    }

    if (currentOption) {
      setValue(
        'paymentAccount',
        {
          id: currentOption.id,
          label: currentOption.label,
        },
        {
          shouldDirty: false,
          shouldValidate: true,
        },
      );

      return;
    }

    const initialPaymentAccountId = deductionData?.vendorPaymentAccountId
      ? String(deductionData.vendorPaymentAccountId)
      : deductionData?.paymentAccountId
        ? String(deductionData.paymentAccountId)
        : '';

    const initialOption = options.find(
      (item) => item.id === initialPaymentAccountId,
    );

    if (initialOption) {
      setValue(
        'paymentAccount',
        {
          id: initialOption.id,
          label: initialOption.label,
        },
        {
          shouldDirty: false,
          shouldValidate: true,
        },
      );

      return;
    }

    const defaultAccountId = getVendorDefaultPaymentAccountId(
      vendorForPaymentAccounts,
      paymentMethodValue,
    );

    if (defaultAccountId) {
      const defaultOption = options.find(
        (item) => item.id === defaultAccountId,
      );

      if (defaultOption) {
        setValue(
          'paymentAccount',
          {
            id: defaultOption.id,
            label: defaultOption.label,
          },
          {
            shouldDirty: false,
            shouldValidate: true,
          },
        );

        return;
      }
    }

    setValue('paymentAccount', null, {
      shouldDirty: false,
      shouldValidate: true,
    });

  }, [vendorAccountIdValue, paymentMethodValue, activeVendors, vendorPaymentAccountsById, paymentAccountValue?.id, deductionData, setValue, requiresPaymentAccount]);

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return;
    }

    const previousDisposition = prevDispositionRef.current;

    if (previousDisposition === dispositionValue) {
      return;
    }

    if (dispositionValue === 'External') {
      setValue('paymentMethod', '', {
        shouldDirty: true,
      });

      setValue('paymentAccount', null, {
        shouldDirty: true,
        shouldValidate: true,
      });

      setValue('externalPaymentReference', '', {
        shouldDirty: true,
      });

      setValue('externalPaymentDate', null, {
        shouldDirty: true,
      });
    }

    if ((dispositionValue === 'Pay' || dispositionValue === 'Withhold') && defaultPaymentMethod) {
      setValue('paymentMethod', defaultPaymentMethod, {
        shouldDirty: true,
      });
    }

    prevDispositionRef.current = dispositionValue;
  }, [dispositionValue, defaultPaymentMethod, setValue]);

  useEffect(() => {
    if (!isAssigned) {
      setValue('defendant', {
        id: '',
        label: '',
      });
    }
  }, [isAssigned, setValue]);

  useEffect(() => {
    if (typeFieldValue !== 'Lien' && typeFieldValue !== 'SERVICE_PROVIDER' && vendorAccountValue !== null) {
      setValue('vendorAccount', null, {
        shouldDirty: true,
      });

      setValue('paymentAccount', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    formMethods.trigger(['vendorAccount']);
  }, [typeFieldValue, vendorAccountValue, setValue]);

  useEffect(() => {
    formMethods.trigger('amount');
  }, [preSettlementAmountValue, formMethods]);

  useEffect(() => {
    if (preSettlementAmountValue && settlementAmountValue !== undefined) {
      formMethods.trigger('preSettlementAmount');
    }
  }, [settlementAmountValue, formMethods, preSettlementAmountValue]);

  const handleVendorSelectFromNetSuite = async (_vendorName: string, _vendorId: number, vendorInternalId?: string) => {
    setIsSelectingNetSuiteVendor(true);

    try {
      let vendorTypeFilter: string | undefined;

      if (typeFieldValue === 'Lien') {
        vendorTypeFilter = 'LIEN_HOLDER';
      } else if (typeFieldValue === 'SERVICE_PROVIDER') {
        vendorTypeFilter = 'SERVICE_PROVIDER';
      }

      const filters = vendorTypeFilter
        ? {
          vendorType: vendorTypeFilter,
        }
        : undefined;

      const response = await getActiveVendorAccounts('', filters);

      const freshVendors = response.data as typeof activeVendors;

      const matchedVendor = freshVendors.find((vendor) => vendor.accounting?.externalVendorId === vendorInternalId);

      if (matchedVendor) {
        setActiveVendors(freshVendors);
        // setValue('paymentMethod', matchedVendor.defaultVendorPaymentMethod || '', {
        //   shouldDirty: true,
        // });
        setValue(
          'vendorAccount',
          {
            id: String(matchedVendor.id),
            label: matchedVendor.name,
            defaultPaymentMethod: matchedVendor.defaultVendorPaymentMethod,
            accounting: matchedVendor.accounting || undefined,
          },
          {
            shouldDirty: true,
          },
        );

        await applyVendorPaymentDefaults(
          {
            id: String(matchedVendor.id),
            label: matchedVendor.name,
            defaultPaymentMethod: matchedVendor.defaultVendorPaymentMethod,
            accounting: matchedVendor.accounting,
          },
          true,
        );
      }
    } finally {
      setIsSelectingNetSuiteVendor(false);
    }
  };

  const handleClose = () => {
    onCancel();

    setDeductionId(null);
    setDeductionData(undefined);

    setRemittanceContext(null);
    setRemittanceTemplate(null);
    setIsContextLoading(false);
    setHasRequestedRemittanceContext(false);

    isInitialLoadRef.current = true;
  };

  const handleTypeChange = (value: string) => {
    setValue('type', value, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('vendorAccount', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue('paymentAccount', null, {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (dispositionValue !== 'External') {
      setValue('paymentMethod', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    // const isNextVendorType =
    //   value === 'Lien' ||
    //   value === 'SERVICE_PROVIDER';

    // if (!isNextVendorType) {
    //   setRemittanceLines([]);
    //   remittanceLinesRef.current = [];
    // }
  };

  const handleDownloadFile = async () => {
    try {
      const response = await getDocument(deductionId as string);

      if (response.data?.downloadUrl) {
        const fileUrl = response.data.downloadUrl;

        const link = document.createElement('a');

        link.href = fileUrl;
        link.download = '';
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setAlert((prev) => ({
          ...prev,
          message: 'No file found to download.',
          type: 'info',
        }));
      }
    } catch (error) {
      setAlert((prev) => ({
        ...prev,
        message: 'File download error',
        type: 'info',
      }));
    }
  };

  const handleDeleteFile = async () => {
    try {
      await deleteDocument(deductionId as string);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setValue('uploadInvoice', null as any, {
        shouldDirty: true,
      });

      if (deductionData) {
        deductionData.fileName = null;
      }

      setAlert((prev) => ({
        ...prev,
        message: 'Invoice deleted successfully.',
        type: 'success',
      }));
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;

      setAlert((prev) => ({
        ...prev,
        message: axiosError?.response?.data?.message || 'Failed to delete invoice',
        type: 'error',
      }));
    }

    handleCloseDeleteInvoicePopup();
  };

  const submitDeductionForm = (values: DeductionEditFormSubmitProps) => {
    setIsFormSubmitting(true);

    const typeValue = values.type === 'Expense Reimbursement' ? 'Advance' : values.type;

    const formData = new FormData();

    formData.append('defendantId', values.defendant && values.defendant.id ? String(values.defendant.id) : '');

    formData.append('amount', values.amount);

    formData.append('type', typeValue);

    formData.append('disposition', values.disposition || '');

    // formData.append('description', values.description || '');

    formData.append('memo', values.memo || '');

    if (typeValue === 'Lien' || typeValue === 'SERVICE_PROVIDER') {
      formData.append('vendorFirmParticipationId', values.vendorAccount?.id ? String(values.vendorAccount.id) : '');

      if (values.disposition !== 'External') {
        if (values.paymentMethod) {
          formData.append('paymentMethod', values.paymentMethod);
        }

        if (
          ['ACH', 'CHECK'].includes((values.paymentMethod || '').toUpperCase()) &&
          values.paymentAccount?.id
        ) {
          formData.append('vendorPaymentAccountId', String(values.paymentAccount.id));
        }
      }
    }

    if (values.preSettlementAmount) {
      formData.append('preSettlementAmount', values.preSettlementAmount);
    }

    if (values.uploadInvoice) {
      formData.append('document', values.uploadInvoice);
    }

    if (values.entryDate) {
      formData.append('entryDate', moment(values.entryDate).toISOString());
    }

    if (dispositionValue === 'External') {
      formData.append('paymentMethod', values.paymentMethod || '');

      formData.append('externalPaymentReference', values.externalPaymentReference || '');

      const formattedDate =
        values.externalPaymentDate instanceof Date ? values.externalPaymentDate.toISOString() : values.externalPaymentDate || '';

      formData.append('externalPaymentDate', formattedDate);
    }

    const originalRemittanceLines = Array.isArray(
      (
        deductionData as
        | {
          remittanceLines?: RemittanceLine[];
        }
        | undefined
      )?.remittanceLines,
    )
      ? (
        deductionData as {
          remittanceLines: RemittanceLine[];
        }
      ).remittanceLines
      : [];

    const isVendorTypeAfterUpdate = typeValue === 'Lien' || typeValue === 'SERVICE_PROVIDER';

    const isVendorCheckRemittance = isVendorTypeAfterUpdate && (values.paymentMethod || '').toUpperCase() === 'CHECK';

    const hasRemittanceLineChanges = !!dirtyFields.remittanceLines;

    const hasNewDefaultRows =
      originalRemittanceLines.length === 0 && Array.isArray(remittanceLinesValue) && remittanceLinesValue.length > 0;

    const shouldSubmitRemittanceLines = hasRemittanceLineChanges || hasNewDefaultRows;

    const shouldClearExistingRemittance = !isVendorCheckRemittance && originalRemittanceLines.length > 0;

    if (isVendorCheckRemittance && shouldSubmitRemittanceLines) {
      const payloadRemittanceLines = remittanceLinesValue.map(({ amount, values }) => ({
        amount,
        values,
      }));

      formData.append('remittanceLines', JSON.stringify(payloadRemittanceLines));
    }

    if (shouldClearExistingRemittance) {
      formData.append('remittanceLines', JSON.stringify([]));
    }

    updateDeductionRecord(values.id as string, formData)
      .then((res) => {
        if (!res.data.error) {
          setIsFormSubmitting(false);

          onSuccess?.();
          setDeductionId(null);

          setAlert((prev) => ({
            ...prev,
            message: 'Deduction updated successfully.',
            type: 'success',
          }));
        }
      })
      .catch((err: AxiosError<ErrorData>) => {
        setIsFormSubmitting(false);

        setAlert((prev) => ({
          ...prev,
          message: err?.response?.data?.message || 'Failed to update deduction',
          type: 'error',
        }));
      })
  };

  const onSubmitForm = (values: DeductionEditFormSubmitProps) => {
    if (selectedPaymentAccount?.isFailedAccount) {
      pendingSubmitValuesRef.current = values;
      setIsFailedAccountConfirmOpen(true);
      return;
    }

    submitDeductionForm(values);
  };

  const handleConfirmFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);

    const values = pendingSubmitValuesRef.current;
    pendingSubmitValuesRef.current = null;

    if (values) {
      submitDeductionForm(values);
    }
  };

  const handleCancelFailedAccountSubmit = () => {
    setIsFailedAccountConfirmOpen(false);
    pendingSubmitValuesRef.current = null;
  };

  const onInvalidSubmit = () => {
    setAlert((prev) => ({
      ...prev,
      message: 'Please fill all required fields before submitting.',
      type: 'error',
    }));
  };

  return (
    <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose} scroll="paper">
      <Box display="flex" justifyContent="flex-end">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disableRipple
          sx={(theme) => ({
            color: theme.palette.common.white,
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box display="flex" alignItems="center" mb="16px" gap="16px">

        <DialogTitle>Edit Deduction</DialogTitle>
        {deductionId && <FormLinkButton
          path={deductionId ? `/payments/deduction/${Number(deductionId)}/home` : '#'}
          disabled={!deductionId}
          onClick={handleClose}
        />}
      </Box>

      {isPopupLoading ? (
        <Box sx={{ margin: '30px' }}>
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <DialogContent>
            <FormProvider {...formMethods}>
              <Box className={classes.formContainer}>
                <Box className={classes.formFieldsWrapper}>
                  <Box display="flex" gap="16px" alignItems="center" width="800px">
                    <Box mt="10px">
                      <FormAutocompleteFieldServerSearch
                        key={caseId ? String(caseId) : 'no-case'}
                        name="defendant"
                        label="Defendant"
                        optionsQuery={handleGetDefendants}
                        mapQueryResults={mapDefendantsQueryResults}
                        disabled={!isAssigned || !canEdit}
                        filters={{
                          caseId: Number(caseId),
                        }}
                        width="633px"
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        fetchSize={100}
                        minSearchChars={3}
                      />
                    </Box>

                    <FormToggleField name="isAssigned" label="Assigned" disabled={!canEdit} />

                    {canViewDefendants && (
                      <FormLinkButton
                        path={caseId ? `/defendants/${Number(defendantIdValue)}/home` : '#'}
                        disabled={!defendantIdValue}
                      />
                    )}
                  </Box>

                  <FormTextField name="preSettlementAmount" label="Billed Amount" valueDefinition="$" disabled={!canEdit} />

                  <Box display="flex" alignItems="center" gap="8px" width="100%">
                    <Box flex={1}>
                      <FormTextField name="amount" label="Settled Amount" valueDefinition="$" disabled={!canEdit} />
                    </Box>

                    {/* {shouldWarnRemittanceUpdate && (
                      <Box display="flex" alignItems="center" gap="4px">
                        <Tooltip
                          title="Settled amount changed. Update remittance before saving when multiple rows are present."
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#35394B',
                                color: '#FFD900',
                                border: '1px solid #FFD900',
                                borderRadius: '10px',
                                boxShadow: '3',
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontWeight: 500,
                                lineHeight: '120%',
                                padding: '8px',
                                maxWidth: '300px',
                              },
                            },
                            arrow: {
                              sx: {
                                color: '#FFD900',
                              },
                            },
                          }}
                        >
                          <InfoIcon
                            sx={{
                              fontSize: '20px',
                              color: '#FFD900',
                              cursor: 'pointer',
                            }}
                          />
                        </Tooltip>

                        <FormLinkButton
                          path={deductionId ? `/payments/deduction/${Number(deductionId)}/home` : '#'}
                          disabled={!deductionId}
                          onClick={handleClose}
                        />

                      </Box>


                    )} */}
                  </Box>

                  <FormSelectField
                    name="type"
                    label="Type *"
                    options={DEDUCTION_TYPES}
                    width="100%"
                    disabled={!canEdit}
                    customOnChange={(value) => handleTypeChange(value)}
                  />

                  <FormDateField name="entryDate" label="Entry Date" maxDate={moment()} disabled={!canEdit} />

                  <FormTextField name="description" label="Description" isMultiline isDescription disabled={!canEdit} />

                  {isVendorType && (
                    <>
                      <FormAutocompleteFieldServerSearch
                        key={`vendor-${typeFieldValue}`}
                        name="vendorAccount"
                        label="Vendor Account *"
                        width="800px"
                        disabled={!canEdit}
                        optionsQuery={(params) => handleGetActiveVendors(params, typeFieldValue)}
                        mapQueryResults={mapVendorsQueryResults}
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        onValueChange={(option) => {
                          const selectedVendorId = String(option?.id || '');
                          const currentVendor = getValues('vendorAccount');
                          const currentVendorId = String(currentVendor?.id || '');
                          const currentPaymentMethod = getValues('paymentMethod');
                          const currentPaymentAccount = getValues('paymentAccount');
                          const shouldPreserveSavedPaymentDetails =
                            !isDirty &&
                            !!selectedVendorId &&
                            selectedVendorId === currentVendorId &&
                            !!currentPaymentMethod &&
                            !!currentPaymentAccount?.id;

                          if (shouldPreserveSavedPaymentDetails) {
                            return;
                          }

                          void applyVendorPaymentDefaults(option, true);
                        }}
                        fetchSize={100}
                        minSearchChars={3}
                      />

                      {canViewVendors &&
                        vendorAccountValue?.id &&
                        vendorAccountValue?.accounting &&
                        !vendorAccountValue?.accounting?.isSynced && (
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-start"
                            gap="8px"
                            width="92%"
                            sx={{
                              background: (theme) => theme.palette.primary.light,
                              color: (theme) => theme.palette.common.white,
                              fontFamily: 'Inter',
                              fontSize: 15,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap="8px">
                              <span>{vendorAccountValue.label}</span>

                              <Box
                                sx={{
                                  backgroundColor: 'warning.main',
                                  color: 'white',
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Unsynced
                              </Box>
                            </Box>

                            <FormLinkButton path={`/vendors/edit-vendor-account/${vendorAccountValue.id}`} />
                          </Box>
                        )}

                      {canEdit && isAccountingIntegrated && (
                        <Box display='flex' justifyContent='center' gap='8px'>

                          <CommonButton
                            type="infoMain"
                            buttonText= {`Find vendor in ${vendorEntity}`}
                            onButtonClick={() => setIsNetSuitePopupOpen(true)}
                            minWidth='500px'
                          />

                          {/* <Button
                          variant="contained"
                          onClick={() => setIsNetSuitePopupOpen(true)}
                          sx={{
                            boxSizing: 'border-box',
                            width: '100%',
                            padding: '12px 16px',
                            backgroundColor: theme.palette.info.main,
                            color: 'white',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontFamily: 'Inter',
                            fontSize: '16px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '120%',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              backgroundColor: theme.palette.info.dark,
                            },
                          }}
                        >
                          Find vendor in {vendorEntity}
                        </Button> */}
                        </Box>
                      )}

                      <Box
                        sx={{
                          width: '100%',
                        }}
                        display='flex'
                        justifyContent='center'
                      >
                        <FormPDFUploadField
                          name="uploadInvoice"
                          label="Upload Invoice"
                          existingFileName={deductionData?.fileName as string | undefined}
                          onDownload={handleDownloadFile}
                          onDelete={onDeleteInvoiceButtonClick}
                          minWidth="500px"
                          disabled={!canEdit}
                        />
                      </Box>

                      {vendorAccountIdValue && (
                        <>
                          <Typography
                            variant="h6"
                            sx={{
                              color: '#fff',
                              fontWeight: 600,
                              alignSelf: 'flex-start',
                              marginBottom: '8px',
                              fontSize: '18px',
                            }}
                          >
                            Payment
                          </Typography>

                          <FormSelectField
                            name="disposition"
                            label="Intent *"
                            options={INTENT_TYPE_OPTIONS}
                            width="100%"
                            disabled={!canEdit}
                          />

                          <FormSelectField
                            name="paymentMethod"
                            label={dispositionValue === 'External' ? 'Payment Method' : 'Payment Method *'}
                            options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                            width="100%"
                            disabled={!canEdit}
                            shouldClearable={dispositionValue === 'External'}
                          />
                          {requiresPaymentAccount && paymentMethodValue && (
                            <>
                              <FormSelectField
                                name='paymentAccount.id'
                                label='Account *'
                                options={paymentAccountOptions}
                                width='100%'
                                renderSelectedValue={renderPaymentAccountSelectedValue}
                                renderOption={renderPaymentAccountOption}
                                customOnChange={(value) => {
                                  const selected = paymentAccountOptions.find((item) => item.id === value);
                                  setValue(
                                    'paymentAccount',
                                    selected
                                      ? { id: selected.id, label: selected.label }
                                      : null,
                                    { shouldDirty: true, shouldValidate: true },
                                  );
                                }}
                              />

                              {renderPaymentAccountInfo(
                                selectedPaymentAccount,
                                vendorAccountIdValue,
                              )}
                            </>
                          )}
                          {((requiresPaymentAccount && isPaymentMethodMissing) ||
                            (requiresPaymentAccount && isPaymentProvisioningIssue)) && dispositionValue !== 'External' && (
                              <Box display="flex" flexDirection="row" alignItems="center" gap="8px" width="100%">
                                <Typography color="error" variant="body2" mt='8px'>
                                  {isPaymentMethodMissing
                                    ? 'Missing Account Details'
                                    : 'Some issues with the passport provisioning.'}
                                </Typography>
                                {canViewVendors && <FormLinkButton path={`/vendors/edit-vendor-account/${vendorAccountIdValue}`} />}
                              </Box>
                            )}


                          {isCheckPayment && hasRequestedRemittanceContext && (

                            <>
                              <Box display='flex' flexDirection='column' width='100%' >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: '#fff',
                                    fontWeight: 600,
                                    alignSelf: 'flex-start',
                                    marginBottom: '8px',
                                    fontSize: '18px',
                                  }}
                                >
                                  Check Memo
                                </Typography>
                                <Typography fontSize='12px' color='text.secondary' mt='2px'>
                                  This prints on the memo line of the check.
                                </Typography>
                              </Box>

                              <FormTextField name='memo' label='Check Memo' isMultiline isDescription disabled={!canEdit} />


                              <Box mt="8px" width="100%">
                                <RemittanceAccordion
                                  remittanceContext={remittanceContext}
                                  remittanceLines={remittanceLinesValue}
                                  deductionAmount={amountValue}
                                  onChangeRemittanceLines={(lines) => {
                                    setValue('remittanceLines', lines, {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    });
                                  }}
                                  isLoading={isContextLoading}
                                  canUpdate={canUpdate}
                                  onFormOpenChange={setIsRemittanceFormOpen}
                                />
                              </Box>
                            </>

                          )}
                        </>
                      )}
                    </>
                  )}

                  {typeFieldValue === 'Expense Reimbursement' && (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#fff',
                          fontWeight: 600,
                          alignSelf: 'flex-start',
                          marginBottom: '8px',
                          fontSize: '18px',
                        }}
                      >
                        Payment
                      </Typography>

                      <FormSelectField
                        name="disposition"
                        label="Intent *"
                        options={INTENT_TYPE_OPTIONS}
                        width="100%"
                        disabled={!canEdit}
                      />
                    </>
                  )}

                  {dispositionValue === 'External' && typeFieldValue === 'Expense Reimbursement' && (
                    <FormSelectField
                      name="paymentMethod"
                      label="Payment Method"
                      options={DEDUCTION_PAYMENT_METHOD_OPTIONS}
                      width="100%"
                      disabled={!canEdit}
                      shouldClearable
                    />
                  )}

                  {dispositionValue === 'External' &&
                    ((isVendorType && vendorAccountIdValue) || typeFieldValue === 'Expense Reimbursement') && (
                      <>
                        <FormTextField name="externalPaymentReference" label="Payment Reference" disabled={!canEdit} />

                        <FormDateField name="externalPaymentDate" label="Payment Date" maxDate={moment()} disabled={!canEdit} />
                      </>
                    )}

                  <Box className={classes.settlementSummaryBox}>
                    <Typography
                      sx={{
                        color: '#fff',
                        alignSelf: 'flex-start',
                        marginBottom: '8px',
                        fontSize: '18px',
                        paddingBottom: '5px',
                        fontWeight: 500,
                      }}
                    >
                      Settlement Summary
                    </Typography>

                    <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" gap={4}>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Typography
                          sx={{
                            color: '#ffffff',
                            fontFamily: 'Inter',
                          }}
                        >
                          Billed Amount
                        </Typography>

                        <Typography
                          sx={{
                            color: '#ffffff',
                            fontFamily: 'Inter',
                          }}
                        >
                          Settled Amount
                        </Typography>
                      </Box>

                      <Box display="flex" flexDirection="column" gap={2} alignItems="flex-end">
                        <Typography
                          sx={{
                            color: '#fff',
                            fontFamily: 'Inter',
                          }}
                        >
                          {numberToUSDCurrency(formMethods.watch('preSettlementAmount')) || '-'}
                        </Typography>

                        <Typography
                          sx={{
                            color: '#fff',
                            fontFamily: 'Inter',
                          }}
                        >
                          {numberToUSDCurrency(formMethods.watch('amount')) || '-'}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      className={classes.borderedBox}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Box>
                        <Typography
                          sx={{
                            color: '#ffffffff',
                            fontSize: '16px',
                            fontWeight: 500,
                            paddingBottom: '2px',
                            fontFamily: 'Inter',
                          }}
                        >
                          Amount Payable to Plaintiff
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.success.main,
                            fontSize: '14px',
                            fontFamily: 'Inter',
                          }}
                        >
                          {(() => {
                            const billed = parseFloat(formMethods.watch('preSettlementAmount') || '0') || 0;
                            const agreed = parseFloat(formMethods.watch('amount') || '0') || 0;
                            const savings = billed - agreed;
                            return savings > 0 ? `Plaintiff saves $${savings.toFixed(2)} from lien reduction` : '';
                          })()}
                        </Typography>
                      </Box>

                      <Typography
                        sx={{
                          color: theme.palette.success.main,
                          fontWeight: 600,
                          fontSize: '16px',
                          fontFamily: 'Inter',
                        }}
                      >
                        {(() => {
                          const billed = parseFloat(formMethods.watch('preSettlementAmount') || '0') || 0;
                          const agreed = parseFloat(formMethods.watch('amount') || '0') || 0;
                          const savings = billed - agreed;
                          return `+$${savings.toFixed(2)}`;
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <DialogActions className={classes.popupActions}>
                  {canEdit && (
                    <>
                      <FormSubmitButton
                        disabled={
                          !isValid ||
                          !isDirty ||
                          isRemittanceFormOpen ||
                          (isAssigned && !(watch('defendant') && watch('defendant').id)) ||
                          isVendorAccountMissing ||
                          !isCheckRemittanceBalanced ||
                          isFormSubmitting ||
                          (isVendorType && Boolean(vendorAccountValue?.accounting && !vendorAccountValue.accounting.isSynced)) ||
                          (isVendorType &&
                            dispositionValue !== 'External' &&
                            (!vendorAccountIdValue ||
                              !paymentMethodValue ||
                              (requiresPaymentAccount && !!isPaymentMethodMissing) ||
                              (requiresPaymentAccount && !!isPaymentProvisioningIssue) ||
                              (requiresPaymentAccount && !paymentAccountValue?.id)))
                        }
                        buttonText="Submit"
                        onClick={handleSubmit(onSubmitForm, onInvalidSubmit)}
                      />

                      <FormCancelButton onClick={handleClose} />
                    </>
                  )}
                </DialogActions>
              </Box>
            </FormProvider>

            <Popup
              isOpen={isDeleteInvoicePopupOpen}
              headlineText={POPUP_DESCRIPTIONS.deductionInvoiceDelete.headlineText}
              contentText={POPUP_DESCRIPTIONS.deductionInvoiceDelete.contentText}
              onMainButtonClick={handleDeleteFile}
              onSecondaryButtonClick={handleCloseDeleteInvoicePopup}
            />

            <Popup
              isOpen={isFailedAccountConfirmOpen}
              headlineText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.headlineText}
              contentText={POPUP_DESCRIPTIONS.deductionFailedAccountConfirm.contentText}
              mainButtonText="Yes"
              secondaryButtonText="No"
              onMainButtonClick={handleConfirmFailedAccountSubmit}
              onSecondaryButtonClick={handleCancelFailedAccountSubmit}
            />
          </DialogContent>
        </>
      )}

      <FindNetSuiteVendorPopup
        open={isNetSuitePopupOpen}
        lienName={vendorAccountValue?.label || ''}
        lienId={Number(vendorAccountValue?.id)}
        searchTerm={vendorAccountValue?.label || ''}
        onClose={() => setIsNetSuitePopupOpen(false)}
        onVendorSelect={handleVendorSelectFromNetSuite}
        synced={vendorAccountValue?.id ? (vendorAccountValue.accounting?.isSynced ? 'isSynced' : 'unsynced') : null}
        entityType="Vendor"
      />
    </Dialog>
  );
};

export default EditDeductionPopup;